import type { Translations, TranslationKey } from './types';
import { en } from './en';
import { de } from './de';
import { fr } from './fr';
import { es } from './es';
import { it } from './it';
import { nl } from './nl';

export type Locale = 'en' | 'de' | 'fr' | 'es' | 'it' | 'nl';

const locales: Record<Locale, Translations> = { en, de, fr, es, it, nl };

export const SUPPORTED_LOCALES: { code: Locale; flag: string; label: string; native: string }[] = [
  { code: 'en', flag: '🇬🇧', label: 'English', native: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'German', native: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', label: 'French', native: 'Français' },
  { code: 'es', flag: '🇪🇸', label: 'Spanish', native: 'Español' },
  { code: 'it', flag: '🇮🇹', label: 'Italian', native: 'Italiano' },
  { code: 'nl', flag: '🇳🇱', label: 'Dutch', native: 'Nederlands' },
];

const STORAGE_KEY = 'app-locale';

/** Detect the best locale: localStorage → navigator.language → 'en' */
export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in locales) return stored as Locale;
  } catch { /* ignore */ }

  const nav = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language.toLowerCase() : '';
  if (nav.startsWith('de')) return 'de';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('nl')) return 'nl';
  return 'en';
}

/** Persist locale choice */
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch { /* ignore */ }
}

/** Create a translate function for the given locale */
export function createTranslate(locale: Locale) {
  const messages = locales[locale];
  const fallback = locales['en'];

  return function t(key: TranslationKey, params?: Record<string, string | number>): string {
    let value = messages[key] ?? fallback[key] ?? key;
    if (typeof value === 'function') {
      return (value as (p?: Record<string, string | number>) => string)(params);
    }
    if (params && typeof value === 'string') {
      for (const [k, v] of Object.entries(params)) {
        value = value.replace(`{${k}}`, String(v));
      }
    }
    return String(value);
  };
}

export type TranslateFn = ReturnType<typeof createTranslate>;
export type { TranslationKey };
